import {
  ApolloClient,
  ApolloLink,
  DefaultContext,
  FetchResult,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
  Operation,
} from '@apollo/client'
import { ErrorResponse, onError } from '@apollo/client/link/error'
import { offsetLimitPagination } from '@apollo/client/utilities'
import { CachePersistor, LocalForageWrapper } from 'apollo3-cache-persist'
import localforage from 'localforage'
import { Monitoring } from '../utils/monitoring'
import { clampClientsPaginationVariables } from './paginationLimit'
import { nestedOffsetLimitPagination } from './nestedOffsetLimitPagination'
import { createSessionExpiryLink } from './session-expiry-link'
import { GetTokenOptions } from '../contexts/auth/token-provider'

export type SessionHandlers = {
  readonly isEnabled: () => boolean
  readonly getToken: (options?: GetTokenOptions) => Promise<string>
  // Returns whether a login redirect is under way.
  readonly onSessionExpired: (operationName: string) => boolean
}

const storage = localforage.createInstance({
  name: 'ayra-apollo-cache',
})

const SCHEMA_VERSION = 'v1'
const SCHEMA_VERSION_KEY = 'apollo-schema-version'
const CACHE_TIMESTAMP_KEY = 'apollo-cache-timestamp'

function injectAuthorizationToken(token: string) {
  return function (context: DefaultContext): DefaultContext {
    return {
      ...context,
      headers: {
        ...context.headers,
        Authorization: token,
      },
    }
  }
}

function createAuthLink(token: string, session?: SessionHandlers): ApolloLink {
  return new ApolloLink(function (operation, forward) {
    if (!session?.isEnabled()) {
      operation.setContext(injectAuthorizationToken(token))
      return forward(operation)
    }

    return new Observable<FetchResult>((observer) => {
      let subscription: { unsubscribe: () => void } | undefined
      let closed = false

      // If MSAL cannot produce a token, send the captured one: the API answers 401 and the
      // session link takes over from there.
      session
        .getToken()
        .catch(() => token)
        .then((currentToken) => {
          if (closed) return
          operation.setContext(injectAuthorizationToken(currentToken))
          subscription = forward(operation).subscribe(observer)
        })

      return () => {
        closed = true
        subscription?.unsubscribe()
      }
    })
  })
}

const createApolloLinkSplit = ApolloLink.split(
  (operation: Operation) => operation.getContext().gateway,
  new HttpLink({
    uri: (operation: Operation) => `${window.ENV.REACT_APP_GATEWAY_ENDPOINT}?q=${operation.operationName}`,
  }),
  new HttpLink({
    uri: (operation: Operation) => `${window.ENV.REACT_APP_API_GRAPHQL_ENDPOINT}?q=${operation.operationName}`,
  }),
)

function graphqlErrorHandler(error: ErrorResponse): Observable<FetchResult> {
  const errorsToMonitor: Error[] = []

  if (error.graphQLErrors) {
    error.graphQLErrors.forEach((e) => {
      errorsToMonitor.push(new Error(e.message, { cause: e }))
    })
  }

  if (error.networkError) errorsToMonitor.push(error.networkError as Error)

  errorsToMonitor.forEach((e) => {
    Monitoring.error(e, {
      kind: 'graphql',
      operation: error.operation.operationName,
      reason: JSON.stringify(e.cause),
    })
  })

  return error.forward(error.operation)
}

function createErrorLink(): ApolloLink {
  return onError(graphqlErrorHandler)
}

function createPaginationLimitLink(): ApolloLink {
  return new ApolloLink(function (operation, forward) {
    const clampedVariables = clampClientsPaginationVariables(operation.operationName, operation.variables)
    if (clampedVariables) {
      operation.variables = clampedVariables
    }
    return forward(operation)
  })
}

export async function getClient(
  token: string,
  session?: SessionHandlers,
): Promise<{
  client: ApolloClient<NormalizedCacheObject>
  persistor: CachePersistor<NormalizedCacheObject>
}> {
  const links = [createErrorLink(), createAuthLink(token, session), createPaginationLimitLink(), createApolloLinkSplit]
  // Outermost, so it sees the outcome after the error link's own resend.
  const apolloLinkOptions = ApolloLink.from(
    session
      ? [
          createSessionExpiryLink({
            isEnabled: session.isEnabled,
            refresh: () => session.getToken({ forceRefresh: true }),
            onSessionExpired: session.onSessionExpired,
          }),
          ...links,
        ]
      : links,
  )

  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          standardProducts: offsetLimitPagination(),
          distributors: nestedOffsetLimitPagination(),
          customers: nestedOffsetLimitPagination(),
          farms: nestedOffsetLimitPagination(),
          fields: nestedOffsetLimitPagination(),
        },
      },
      Customer: {
        fields: {
          farms: nestedOffsetLimitPagination(),
        },
      },
      FarmPreview: {
        fields: {
          fieldsPage: nestedOffsetLimitPagination(),
        },
      },
    },
  })

  const persistor = new CachePersistor({
    cache,
    storage: new LocalForageWrapper(storage),
    maxSize: 10 * 1024 * 1024,
  })

  const CACHE_TTL = 24 * 60 * 60 * 1000 // 24h
  const currentVersion = localStorage.getItem(SCHEMA_VERSION_KEY)
  const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY)
  const isCacheExpired = !cacheTimestamp || Date.now() - Number(cacheTimestamp) > CACHE_TTL

  // Do not restore Apollo cache from localforage: stale GetMe (e.g. isOnboarded) broke onboarding.
  // In-memory cache still applies per session; logout still purges via AuthProviderAzure.
  if (currentVersion !== SCHEMA_VERSION || isCacheExpired) {
    localStorage.setItem(SCHEMA_VERSION_KEY, SCHEMA_VERSION)
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString())
  }

  const client = new ApolloClient({
    cache,
    link: apolloLinkOptions,
    devtools: {
      name: 'ayra',
      enabled: window.ENV.REACT_APP_ENV_NAME !== 'production',
    },
    queryDeduplication: true,
    name: 'new-ayra',
    version: window.ENV.REACT_APP_VERSION,
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'ignore',
      },
      query: {
        errorPolicy: 'none',
      },
      mutate: {
        errorPolicy: 'none',
      },
    },
  })
  return {
    client,
    persistor,
  }
}
